import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  ActionGroup,
  Alert,
  Button,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalVariant,
  Stack,
  StackItem,
  TextArea,
  TextInput,
} from '@patternfly/react-core';

import { useActiveAlertQuery, useAlertMutations } from '@app/AppLayout/useActiveAlert';
import { useAdminAlertPreview } from '@app/Admin/Alerts/AdminAlertPreviewContext';

const LEVEL_OPTIONS = ['info', 'warning', 'danger', 'success'] as const;

const AlertsAdminPage: React.FunctionComponent = () => {
  const { setPreviewAlert, registerPreviewDismissSideEffect } = useAdminAlertPreview();
  const activeQuery = useActiveAlertQuery();

  const [message, setMessage] = useState('');
  const [level, setLevel] = useState<string>('info');
  const [link, setLink] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);

  const active = activeQuery.data ?? null;

  useEffect(() => {
    registerPreviewDismissSideEffect(() => {
      setShowPreview(false);
    });
    return () => registerPreviewDismissSideEffect(null);
  }, [registerPreviewDismissSideEffect]);

  useEffect(() => {
    if (!activeQuery.isSuccess) return;
    const d = activeQuery.data;
    if (d) {
      setMessage(d.message);
      setLevel(d.level);
      setLink(d.link ?? '');
    } else {
      setMessage('');
      setLevel('info');
      setLink('');
    }
  }, [activeQuery.isSuccess, activeQuery.data]);

  useEffect(() => {
    if (!message.trim()) {
      setShowPreview(false);
      setPreviewAlert(null);
      return;
    }
    if (!showPreview) {
      setPreviewAlert(null);
      return;
    }
    setPreviewAlert({
      message: message.trim(),
      link: link.trim() === '' ? null : link.trim(),
      level,
    });
  }, [showPreview, message, level, link, setPreviewAlert]);

  useEffect(
    () => () => {
      setPreviewAlert(null);
    },
    [setPreviewAlert]
  );

  const clearPreview = useCallback(() => {
    setShowPreview(false);
    setPreviewAlert(null);
  }, [setPreviewAlert]);

  const { publishMutation, updateMutation, disableMutation } = useAlertMutations(
    { message, level, link },
    {
      onSuccess: () => {
        setFormError(null);
        clearPreview();
      },
      onDisableSuccess: () => {
        setFormError(null);
        setConfirmDisableOpen(false);
        clearPreview();
      },
      onError: (msg) => setFormError(msg),
    }
  );

  const busy = publishMutation.isPending || updateMutation.isPending || disableMutation.isPending;

  const onPublish = () => {
    setFormError(null);
    if (!message.trim()) {
      setFormError('Message is required.');
      return;
    }
    publishMutation.mutate();
  };

  const onUpdate = () => {
    setFormError(null);
    if (!active) return;
    if (!message.trim()) {
      setFormError('Message is required.');
      return;
    }
    updateMutation.mutate(active.id);
  };

  const hasStatusAlerts = Boolean(formError) || activeQuery.isError;

  if (activeQuery.isLoading) {
    return (
      <section className="dx-page-section secrets-page admin-alerts-page">
        <div className="secrets-page-header">
          <h1 className="secrets-page-title">Alerts</h1>
          <p className="secrets-page-subtitle">
            Publish or update the dashboard-wide banner alert. Changes apply to all signed-in users.
          </p>
        </div>
        <div className="admin-alerts-page__main">
          <div className="dx-empty-state">
            <div className="dx-empty-state__icon">
              <div className="dx-spinner" />
            </div>
            <h4 className="dx-empty-state__title">Loading current alert</h4>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="dx-page-section secrets-page admin-alerts-page">
      <div className="secrets-page-header">
        <h1 className="secrets-page-title">Alerts</h1>
        <p className="secrets-page-subtitle">
          Publish or update the dashboard-wide banner alert. Changes apply to all signed-in users.
        </p>
      </div>

      <div className="admin-alerts-page__main">
        <Stack hasGutter>
          {hasStatusAlerts ? (
            <StackItem>
              <Stack hasGutter>
                {formError ? (
                  <StackItem>
                    <Alert variant="danger" title="Request failed" isInline>
                      {formError}
                    </Alert>
                  </StackItem>
                ) : null}
                {activeQuery.isError ? (
                  <StackItem>
                    <Alert variant="warning" title="Could not load current alert" isInline>
                      {(activeQuery.error as Error).message}
                    </Alert>
                  </StackItem>
                ) : null}
              </Stack>
            </StackItem>
          ) : null}

          <StackItem>
            <Form isWidthLimited maxWidth="36rem" aria-label="Alert configuration">
              <FormGroup label="Message" isRequired fieldId="admin-alert-message">
                <TextArea
                  id="admin-alert-message"
                  value={message}
                  onChange={(_, v) => setMessage(v)}
                  rows={4}
                  isDisabled={busy}
                  placeholder="Short message shown in the sticky banner"
                />
              </FormGroup>

              <FormGroup label="Level" isRequired fieldId="admin-alert-level">
                <FormSelect
                  id="admin-alert-level"
                  value={level}
                  onChange={(_, v) => setLevel(v)}
                  isDisabled={busy}
                  aria-label="Alert level"
                >
                  {LEVEL_OPTIONS.map((opt) => (
                    <FormSelectOption key={opt} value={opt} label={opt} />
                  ))}
                </FormSelect>
              </FormGroup>

              <FormGroup label="Link (optional)" fieldId="admin-alert-link">
                <TextInput
                  id="admin-alert-link"
                  type="url"
                  value={link}
                  onChange={(_, v) => setLink(v)}
                  isDisabled={busy}
                  placeholder="https://..."
                />
                <HelperText component="div">
                  <HelperTextItem variant="indeterminate">
                    Opens in a new tab when the banner is clicked.
                  </HelperTextItem>
                </HelperText>
              </FormGroup>

              <ActionGroup>
                {active ? (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => setShowPreview((v) => !v)}
                      isDisabled={!message.trim() || busy}
                      title="Shows or hides a draft of this alert in the sticky banner at the top of the app. Only you see it."
                    >
                      {showPreview ? 'Hide preview' : 'Show preview'}
                    </Button>
                    <Button variant="primary" onClick={onUpdate} isDisabled={busy} isLoading={updateMutation.isPending}>
                      Save changes
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => setConfirmDisableOpen(true)}
                      isDisabled={busy}
                      isLoading={disableMutation.isPending}
                    >
                      Disable alert
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => setShowPreview((v) => !v)}
                      isDisabled={!message.trim() || busy}
                      title="Shows or hides a draft of this alert in the sticky banner at the top of the app. Only you see it."
                    >
                      {showPreview ? 'Hide preview' : 'Show preview'}
                    </Button>
                    <Button variant="primary" onClick={onPublish} isDisabled={busy} isLoading={publishMutation.isPending}>
                      Publish alert
                    </Button>
                  </>
                )}
              </ActionGroup>
            </Form>
          </StackItem>
        </Stack>
      </div>

      <Modal
        variant={ModalVariant.small}
        title="Disable this alert?"
        isOpen={confirmDisableOpen}
        onClose={() => !disableMutation.isPending && setConfirmDisableOpen(false)}
        actions={[
          <Button
            key="confirm"
            variant="danger"
            onClick={() => active && disableMutation.mutate(active.id)}
            isLoading={disableMutation.isPending}
          >
            Disable
          </Button>,
          <Button
            key="cancel"
            variant="link"
            onClick={() => setConfirmDisableOpen(false)}
            isDisabled={disableMutation.isPending}
          >
            Cancel
          </Button>,
        ]}
      >
        Users will no longer see this banner. You can publish a new alert later.
      </Modal>
    </section>
  );
};

export { AlertsAdminPage };
